/**
 * SA-1387 (spec §5.4): the Routing tab. A router shows its editor at once; a
 * mailbox on another sink says what it does and offers Use a router, which
 * opens the editor with the switch warning. The editor's key is the
 * mailbox and the page's counter, never anything the list read returns, so a
 * refetch never drops a draft. Spec §5.7: the editor reports its unsaved edits
 * through `onDirtyChange`, and this tab reports `false` whenever that editor
 * goes (closed, started again, or the tab or the pane moving on).
 */
import { useEffect } from 'react'
import { Button } from '@/components/ui'
import type { Mailbox, MailboxHandler, PolicyPreview } from '@/services/mailboxes'
import { RouterPolicyEditor } from './RouterPolicyEditor'
import { EMPTY_POLICY, type PolicyDoc } from './routerPolicyModel'
import { USE_A_ROUTER, type LastChange } from './mailboxesText'

interface MailboxRoutingProps {
  mailbox: Mailbox
  /** A mailbox that is not a router: whether Use a router has opened the editor. */
  editorOpen: boolean
  /** Bumped by the page after Cancel and a discard, so the editor starts again from the stored policy. */
  editorKey: number
  /** `sinkLabel(...)`: what a mailbox that is not a router does now. */
  setup: string
  handlers: MailboxHandler[]
  agendas: Array<{ workflow_id: string; name: string }>
  workflows: Array<{ id: string; name: string }>
  lastChange: LastChange | null
  switchWarning: string | null
  error: string | null
  saving: boolean
  onOpenEditor: () => void
  /** SA-1080: the server's check of a policy before the review shows. */
  onPreview: (doc: PolicyDoc) => Promise<PolicyPreview>
  /** Answers `'policy_changed'` when the stored policy moved after the check. */
  onSave: (doc: PolicyDoc, expected: PolicyDoc) => void | Promise<'policy_changed' | void>
  onCancel: () => void
  /** Spec §5.7: whether the editor holds unsaved edits; a stable callback (the page passes its state setter). */
  onDirtyChange?: (dirty: boolean) => void
}

export function MailboxRouting({
  mailbox: mb, editorOpen, editorKey, setup, handlers, agendas, workflows, lastChange, switchWarning,
  error, saving, onOpenEditor, onPreview, onSave, onCancel, onDirtyChange,
}: MailboxRoutingProps) {
  const editorShown = mb.sink_type === 'router' || editorOpen
  const editorId = `${mb.id}:${editorKey}`
  // The editor reports its edits but not its going: once it leaves, nothing of it is unsaved.
  useEffect(() => {
    if (!editorShown || !onDirtyChange) return
    return () => onDirtyChange(false)
  }, [editorShown, editorId, onDirtyChange])

  if (!editorShown) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-foreground">{setup}</p>
        <Button variant="outline" size="sm" onClick={onOpenEditor}>{USE_A_ROUTER}</Button>
      </div>
    )
  }
  return (
    <RouterPolicyEditor
      key={editorId}
      doc={mb.sink_type === 'router' && mb.sink_config ? mb.sink_config : EMPTY_POLICY}
      handlers={handlers}
      agendas={agendas}
      workflows={workflows}
      lastChange={lastChange}
      switchWarning={switchWarning}
      error={error}
      channel={mb.channel ?? 'email'}
      saving={saving}
      onPreview={onPreview}
      onSave={onSave}
      onCancel={onCancel}
      onDirtyChange={onDirtyChange}
    />
  )
}
